import { useEffect, useState, useSyncExternalStore } from "react";
import {
  bindToolCloseTracking,
  initAnalytics,
  trackToolOpen,
} from "../analytics";
import {
  getPortalAuthErrorCode,
  messageForPortalAuthError,
} from "../lib/portal-auth-errors";
import {
  getDevAccessOverrideSnapshot,
  isDevAccessEnabled,
  subscribeDevAccessOverride,
  type DevAccessOverride,
} from "../lib/dev-access-override";
import type { AccessLevel } from "../portal-auth";

export interface UseAccessState {
  access: AccessLevel | null;
  name: string | null;
  loading: boolean;
  error: string | null;
  isPaid: boolean;
  isFree: boolean;
  devOverride: DevAccessOverride | null;
}

function applyDevOverride(
  portalAccess: AccessLevel | null,
  portalError: string | null,
  override: DevAccessOverride | null
): { access: AccessLevel | null; error: string | null } {
  if (!isDevAccessEnabled() || override === null) {
    return { access: portalAccess, error: portalError };
  }

  if (override === "paid") {
    return { access: "PAID", error: null };
  }
  if (override === "free") {
    return { access: "FREE", error: null };
  }
  return { access: portalAccess, error: portalError };
}

export function useAccess(): UseAccessState {
  const [portalAccess, setPortalAccess] = useState<AccessLevel | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalError, setPortalError] = useState<string | null>(null);

  const devOverride = useSyncExternalStore(
    subscribeDevAccessOverride,
    getDevAccessOverrideSnapshot
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!window.PortalAuth) {
        if (!cancelled) {
          setPortalError("Portal Auth SDK niet geladen.");
          setLoading(false);
        }
        return;
      }

      try {
        await initAnalytics();
        const result = await window.PortalAuth.init();
        if (cancelled) return;
        setPortalAccess(result.access);
        setName(result.name);
        setPortalError(null);

        const slug = import.meta.env.VITE_TOOL_SLUG as string | undefined;
        if (slug) {
          trackToolOpen(slug, result.access);
        }
      } catch (err) {
        if (cancelled) return;
        setPortalAccess("NONE");
        setName(null);
        setPortalError(messageForPortalAuthError(getPortalAuthErrorCode(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const slug = import.meta.env.VITE_TOOL_SLUG as string | undefined;
    if (!slug || portalAccess === null) return;
    return bindToolCloseTracking(slug);
  }, [portalAccess]);

  const { access, error } = applyDevOverride(
    portalAccess,
    portalError,
    devOverride
  );

  return {
    access,
    name,
    loading,
    error,
    isPaid: access === "PAID",
    isFree: access === "FREE",
    devOverride,
  };
}
